"use client";
import { AuthButton } from "@general-components/AuthButton";
import { message } from "@basic-components";
import { useFormContext } from "react-hook-form";
import { type ButtonProps } from "@basic-components";
import { type UseFormReturn } from "react-hook-form";

// 取出错误对象中第一个字段的路径（支持 hallList.0.name 这种嵌套/数组路径）
const getFirstErrorName = (errors: any, parentPath = ""): string | null => {
  if (!errors || typeof errors !== "object") return null;
  for (const key of Object.keys(errors)) {
    const value = errors[key];
    if (!value) continue;
    const path = parentPath ? `${parentPath}.${key}` : key;
    // react-hook-form 的叶子错误节点形如 { type, message, ref }
    if (value.message !== undefined || value.type !== undefined) {
      return path;
    }
    const nested = getFirstErrorName(value, path);
    if (nested) return nested;
  }
  return null;
};

export const FormSubmitButton = ({
  children,
  onClick,
  onError,
  form,
  authKey,
  ...restProps
}: Omit<ButtonProps, "onClick"> & {
  authKey?: string;
  onClick?: (data: any) => void;
  onError?: (errors: any) => void;
  form?: UseFormReturn<any>;
}) => {
  const contextForm = useFormContext();
  const usedForm = form || contextForm;
  const {
    formState: { isSubmitting },
    handleSubmit,
    setFocus,
  } = usedForm;

  // 默认失败处理：让校验失败变可见——提示 + 聚焦/滚动到第一个错误字段。
  // 否则 handleSubmit 在校验不通过时会静默吞掉，表现为“点击没反应、不发请求”。
  const handleError = (errors: any) => {
    if (onError) {
      onError(errors);
      return;
    }
    message.error("请检查表单，仍有必填项未填写或格式有误");
    const firstName = getFirstErrorName(errors);
    if (firstName) {
      try {
        setFocus(firstName as any, { shouldSelect: true });
      } catch {
        // 部分自定义组件未注册 ref，无法聚焦时忽略
      }
    }
  };

  const handleClick = () => {
    if (!onClick) return;
    handleSubmit(onClick, handleError)();
  };

  return (
    <AuthButton
      type="primary"
      authKey={authKey}
      {...restProps}
      onClick={handleClick}
      loading={isSubmitting}
      disabled={isSubmitting}
    >
      {children}
    </AuthButton>
  );
};

export const FormControlButton = ({
  children,
  onClick,
  authKey,
  ...restProps
}: Omit<ButtonProps, "onClick"> & {
  authKey?: string;
  onClick?: (data: any) => void;
}) => {
  const form = useFormContext();
  return (
    <AuthButton
      authKey={authKey}
      {...restProps}
      onClick={onClick ? () => onClick(form) : undefined}
    >
      {children}
    </AuthButton>
  );
};
